/** Puts+ / Calls+ board fed by Tradier — Puts/Calls table format + filters (cutoffs live in Settings). */

import * as Layout from "./wheelScreenerLayout";
import {
  displayTableColumns,
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  renderTablePinnedAttr,
  type ColumnState,
} from "./tableColumns";
import {
  compactSubline,
  isWebCompactMode,
  resolveWebTableColumns,
  subscribeWebCompactMode,
} from "./webCompactMode";
import { loadTableColumns } from "./columnSettings";
import type { TableColumnId } from "./tableColumnRegistry";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  recommendationLedgerMark,
  subscribeLedgerMarks,
} from "./ledgerMarks";
import { scopeAllowsRecommendation, subscribeAccountScope } from "./accountScope";
import {
  fmtPremiumPerDay,
  fmtProfitPct,
  recommendationProfitPct,
  recommendationProfitability,
} from "./profitability";
import { renderFocusRecRowMenu } from "./focusActions";
import {
  renderScreenerExpiryInnerHtml,
  screenerExpiryDateLabel,
  screenerExpiryEarningsTip,
} from "./screenerExpiryCell";
import { renderScreenerSharesCellHtml } from "./screenerSharesCell";
import { indexBadgesForTicker, renderIndexBadgesHtml } from "./indexMembership";
import {
  callStrikeVsAvgCost,
  callStrikeVsAvgTip,
  callStrikeVsAvgValueClass,
  earningsDateTip,
  earningsWithinExpiry,
  fmtEarningsDate,
  fmtEarningsInDays,
  fmtNum,
  fmtUsd,
  fmtWheelRatio,
  loadPositionsSnapshot,
  normalizeTicker,
  peekPositionsSnapshot,
  sharesForTicker,
  type PositionsSnapshot,
  type WheelRecommendation,
} from "./watchlistData";
import {
  advisoryFilterFieldHtml,
  adviceBadgeHtml,
  adviceRowAttrs,
  applyAdviceMarks,
  bindScreenerAdvice,
  screenerAdviceButtonHtml,
  screenerContractKey,
  screenerRowIsAdvised,
} from "./agentAdviceOverlay";
import { isRiskyCallRecommendation } from "./callsPlus";
import {
  defaultCallsRiskyPopRange,
  meetsRiskyPopRange,
  parseCallsRiskyPopRange,
  renderCallsRiskyPopRangeSlider,
  type CallsRiskyPopRange,
} from "./callsRiskyStrike";
import { fetchAgenticSnapshot } from "../api/agenticClient";
import { loadAvailableCashFromAgenticSnapshot } from "./agenticPortfolioCash";
import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "../shared/tickerFilterInput";
import { WHEEL_TICKER_FILTER_EVENT } from "./wheelTickerFilterFromMenu";

export interface TradierWheelSettings {
  minPop: number;
  maxDtePuts: number;
  maxDteCalls: number;
}

export const TRADIER_WHEEL_DEFAULTS: TradierWheelSettings = {
  minPop: 60,
  maxDtePuts: 21,
  maxDteCalls: 21,
};

export const TRADIER_WHEEL_SETTINGS_CHANGED_EVENT = "sfe:tradier-wheel-settings-changed";

export interface TradierWheelRow {
  underlying_ticker: string;
  side: string;
  strike: number;
  expiration_date: string;
  dte?: number;
  bid?: number;
  ask?: number;
  mid?: number;
  last_price?: number;
  pop?: number;
  roi?: number;
  credit_w?: number;
  collateral?: number;
  underlying_price?: number;
  option_symbol?: string;
  holding_avg_cost?: number;
  ready_call_contracts?: number;
  next_earnings_date?: string | null;
  expires_after_earnings?: boolean;
}

export interface TradierWheelView {
  ok: boolean;
  configured?: boolean;
  message?: string;
  side?: string;
  recommendations: TradierWheelRow[];
  settings?: TradierWheelSettings;
  scanned?: number;
  universe?: number;
  scanComplete?: boolean;
  scannedMaxDte?: number;
  updatedAt?: string | null;
}

async function readJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}) },
    ...init,
  });
  const body = (await res.json().catch(() => ({}))) as T & { message?: string };
  if (!res.ok) {
    throw new Error(body.message || `Request failed (${res.status})`);
  }
  return body;
}

export function fetchTradierWheelSettings(): Promise<{
  ok: boolean;
  settings: TradierWheelSettings;
  configured: boolean;
}> {
  return readJson("/tradier/wheel/settings");
}

export function saveTradierWheelSettings(
  settings: TradierWheelSettings,
): Promise<{ ok: boolean; settings: TradierWheelSettings }> {
  return readJson("/tradier/wheel/settings", {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

export function fetchTradierWheel(side: "puts" | "calls"): Promise<TradierWheelView> {
  const path = side === "calls" ? "calls" : "puts";
  return readJson(`/tradier/wheel/recommendations/${path}`);
}

const PUT_TABLE_ID = "web-puts-screener" as const;
const CALL_TABLE_ID = "web-calls-screener" as const;
const PUT_FILTERS_KEY = "sfe-web-tradier-put-filters-v1";
const CALL_FILTERS_KEY = "sfe-web-tradier-call-filters-v1";
const PUT_STARRED_KEY = "sfe-web-put-starred-v1";
const PUT_STARRED_FILTER_KEY = "sfe-web-tradier-put-starred-filter-v1";
const PUT_CASH_LIMIT_FILTER_KEY = "sfe-web-tradier-put-cash-limit-filter-v1";

const PUT_DTE_BUCKETS = [
  { key: "1-7", min: 1, max: 7, label: "1–7" },
  { key: "8-15", min: 8, max: 15, label: "8–15" },
  { key: "16-24", min: 16, max: 24, label: "16–24" },
  { key: "25-35", min: 25, max: 35, label: "25–35" },
];

const CALL_DTE_BUCKETS = [
  { key: "1-7", min: 1, max: 7, label: "1–7" },
  { key: "8-15", min: 8, max: 15, label: "8–15" },
  { key: "16-24", min: 16, max: 24, label: "16–24" },
  { key: "25-35", min: 25, max: 35, label: "25–35" },
  { key: "36-45", min: 36, max: 45, label: "36–45" },
  { key: "46-60", min: 46, max: 60, label: "46–60" },
  { key: "61+", min: 61, max: Infinity, label: "61+" },
];

const DTE_OPTIONS = PUT_DTE_BUCKETS.map((bucket) => ({ value: bucket.key, label: bucket.label }));
const INDEX_OPTIONS = [
  { value: "none", label: "No index" },
  { value: "sp500", label: "S&P 500" },
  { value: "ndx", label: "NASDAQ" },
  { value: "dji", label: "DJI" },
  { value: "rut", label: "Russell" },
];
const RUBRIC_OPTIONS = [
  { value: "Good", label: Layout.displayDecision("Good") },
  { value: "Selective", label: Layout.displayDecision("Selective") },
  { value: "Small size", label: Layout.displayDecision("Small size") },
  { value: "Avoid", label: Layout.displayDecision("Avoid") },
  { value: "Insufficient data", label: Layout.displayDecision("Insufficient data") },
  { value: "__none__", label: "No report" },
  { value: "__unavailable__", label: "Band unavailable" },
  { value: "__unknown__", label: "Lookup unavailable" },
  { value: "__other__", label: "Unmapped band" },
];
const CAP_OPTIONS = [
  { value: "mega", label: "Mega · ≥ $200B" },
  { value: "large", label: "Large · $10B–$200B" },
  { value: "mid", label: "Mid · $2B–$10B" },
  { value: "small", label: "Small · $0.3B–$2B" },
  { value: "micro", label: "Micro · $0.05B–$0.3B" },
  { value: "nano", label: "Nano · under $0.05B" },
  { value: "unknown", label: "Unknown" },
];

const EXTRA_COLUMN_META: Record<
  string,
  {
    label: string;
    num?: boolean;
    heldTh?: boolean;
    hint?: string;
    compactHint?: string;
    sortKey?: string;
    sortType?: string;
  }
> = {
  next_earnings_date: {
    label: "Earnings",
    sortKey: "next_earnings_date",
    sortType: "str",
    hint: "Next earnings date from Tradier corporate calendars. Amber when it lands on or before expiry.",
  },
  sfe_profitability: {
    label: "Profit.",
    num: true,
    hint: "Premium as % of average share cost: last (or mid) ÷ avg cost.",
    compactHint: "Subline: annualized return on the committed share value (Ann. ROC).",
  },
  broker_mark: {
    label: "Br",
    sortKey: "broker_mark",
    sortType: "str",
    hint: "Broker. RH is Robinhood, TT Tastytrade, WB Webull, FD Fidelity.",
  },
  account_short: {
    label: "Ac",
    sortKey: "account_short",
    sortType: "str",
    hint: "Short account name from Settings.",
  },
};

interface PutFilterState {
  quote: "tradable" | "all";
  dte: string[];
  earnings: "before" | "all";
  maxCollateral: number;
  ticker: string;
  sectors: string[];
  rubricBands: string[];
  indexes: string[];
  caps: string[];
  minProfitPct: number;
}

interface CallFilterState {
  quote: "tradable" | "all";
  dte: string;
  earnings: "before" | "all";
  ticker: string;
  shares: "all" | "available";
  sectors: string[];
  rubricBands: string[];
  caps: string[];
  risky: boolean;
  riskyPopRange: CallsRiskyPopRange;
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function isTradableQuote(r: WheelRecommendation): boolean {
  const mid = Number(r.mid);
  const last = Number(r.last_price);
  if (!(mid > 0)) return false;
  const bid = Number(r.bid);
  const ask = Number(r.ask);
  if (!(bid >= 0.01) || !(ask >= bid)) return false;
  if (last > 0) {
    const ref = Math.max(mid, last, 0.01);
    if (Math.abs(mid - last) / ref > 0.15 && Math.abs(mid - last) > 0.1) return false;
  }
  if ((ask - bid) / mid > 0.3) return false;
  return true;
}

function toRec(row: TradierWheelRow, side: "puts" | "calls"): WheelRecommendation {
  const ticker = String(row.underlying_ticker || "").toUpperCase();
  const contracts = Number(row.ready_call_contracts);
  return {
    underlying_ticker: ticker,
    side: side === "calls" ? "call" : "put",
    strike: Number(row.strike),
    expiration_date: row.expiration_date,
    dte: row.dte,
    bid: row.bid,
    ask: row.ask,
    mid: row.mid,
    last_price: row.last_price,
    pop: row.pop,
    roi: row.roi,
    collateral: row.collateral ?? (side === "puts" ? Number(row.strike) * 100 : undefined),
    underlying_price: row.underlying_price,
    option_symbol: row.option_symbol,
    holding_avg_cost: row.holding_avg_cost,
    available_call_contracts: Number.isFinite(contracts) ? contracts : undefined,
    next_earnings_date: row.next_earnings_date ?? null,
    expires_after_earnings: row.expires_after_earnings,
    index_badges: indexBadgesForTicker(ticker),
  } as WheelRecommendation;
}

/** Own-ledger held / available shares — same source as Puts and Calls. */
function enrichShares(
  rec: WheelRecommendation,
  positions: PositionsSnapshot | null,
): WheelRecommendation {
  if (!positions) return rec;
  const ticker = normalizeTicker(rec.underlying_ticker);
  if (!ticker) return rec;
  const { held, available } = sharesForTicker(positions, ticker);
  const contracts =
    available != null && Number.isFinite(available) ? Math.floor(available / 100) : undefined;
  return {
    ...rec,
    sfe_shares_held: held,
    sfe_shares_available: available,
    available_call_contracts:
      contracts != null ? contracts : rec.available_call_contracts,
  };
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function loadLocalStarred(): Set<string> {
  try {
    const raw = localStorage.getItem(PUT_STARRED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set((Array.isArray(parsed) ? parsed : []).map((t) => String(t).toUpperCase()));
  } catch {
    return new Set();
  }
}

function saveLocalStarred(set: Set<string>): void {
  saveJson(PUT_STARRED_KEY, [...set]);
}

function defaultPutFilters(): PutFilterState {
  return Layout.migrateFilterStateV1toV2(
    {
      quote: "all",
      dte: [],
      earnings: "all",
      maxCollateral: 0,
      ticker: "",
      sectors: [],
      rubricBands: [],
      indexes: Layout.DEFAULT_INDEX_FILTER.slice(),
      caps: [],
      minProfitPct: 0,
    },
    "put",
  ) as PutFilterState;
}

function defaultCallFilters(): CallFilterState {
  return Layout.migrateFilterStateV1toV2(
    {
      quote: "all",
      dte: "all",
      earnings: "all",
      ticker: "",
      shares: "all",
      sectors: [],
      rubricBands: [],
      caps: [],
      risky: false,
      riskyPopRange: defaultCallsRiskyPopRange(),
    },
    "call",
  ) as CallFilterState;
}

function loadPutFilters(): PutFilterState {
  const raw = loadJson<Partial<PutFilterState> | null>(PUT_FILTERS_KEY, null);
  if (!raw) return defaultPutFilters();
  return Layout.migrateFilterStateV1toV2(
    {
      ...defaultPutFilters(),
      ...raw,
      ticker: sanitizeTickerFilterValue(raw.ticker ?? "").toUpperCase(),
    },
    "put",
  ) as PutFilterState;
}

function loadCallFilters(): CallFilterState {
  const raw = loadJson<Partial<CallFilterState> | null>(CALL_FILTERS_KEY, null);
  if (!raw) return defaultCallFilters();
  const base = Layout.migrateFilterStateV1toV2(
    { ...defaultCallFilters(), ...raw, ticker: sanitizeTickerFilterValue(raw.ticker ?? "").toUpperCase() },
    "call",
  ) as CallFilterState;
  return {
    ...base,
    riskyPopRange: parseCallsRiskyPopRange(raw.riskyPopRange ?? base.riskyPopRange),
  };
}

function multiSelectOptionsWithAll(
  allLabel: string,
  options: { value: string; label: string }[],
  selected: string[],
): string {
  const allSelected = !selected.length ? " selected" : "";
  const allOpt = `<option value="" data-all="1"${allSelected}>${esc(allLabel)}</option>`;
  const opts = options
    .map((o) => {
      const sel = selected.includes(o.value) ? " selected" : "";
      return `<option value="${escAttr(o.value)}"${sel}>${esc(o.label)}</option>`;
    })
    .join("");
  return allOpt + opts;
}

function dteSelectionForUi(dte: string[]): string[] {
  if (!dte.length || dte.length >= PUT_DTE_BUCKETS.length) return [];
  return dte;
}

function indexesSelectionForUi(indexes: string[]): string[] {
  if (!indexes.length || indexes.length >= Layout.INDEX_FILTER_CODES.length) return [];
  return indexes;
}

function capsSelectionForUi(caps: string[]): string[] {
  if (!caps.length || caps.length >= Layout.CAP_FILTER_CODES.length) return [];
  return caps;
}

function sectorOptions(rows: WheelRecommendation[], selected: string[]): string {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const s = r.sector?.trim() || "Unknown";
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const opts = [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([sector, count]) => {
      const sel = selected.includes(sector) ? " selected" : "";
      return `<option value="${escAttr(sector)}"${sel}>${esc(sector)} (${count})</option>`;
    });
  const allSelected = !selected.length ? " selected" : "";
  return `<option value="" data-all="1"${allSelected}>All sectors</option>${opts.join("")}`;
}

function enhanceMultiSelects(root: HTMLElement): void {
  const api = (window as Window & { UnifiedMultiSelect?: { enhanceAll: (scope?: ParentNode) => void } })
    .UnifiedMultiSelect;
  api?.enhanceAll(root);
}

function readMultiValues(select: HTMLSelectElement | null): string[] {
  if (!select) return [];
  return [...select.selectedOptions]
    .filter((opt) => opt.value !== "" && opt.dataset.all !== "1")
    .map((opt) => opt.value);
}

function isMobileTradierHost(host?: HTMLElement | null): boolean {
  if (typeof document === "undefined") return false;
  if (document.body.classList.contains("sfe-mobile-host")) return true;
  return Boolean(host?.closest(".sfe-mobile-tradier-wheel"));
}

function useCompactCells(host?: HTMLElement | null): boolean {
  return isWebCompactMode() || isMobileTradierHost(host);
}

function resolveTradierColumns(tableId: TableColumnId, mobile: boolean): ColumnState[] {
  if (!mobile) return resolveWebTableColumns(tableId);
  const compactId = `${tableId}-compact` as TableColumnId;
  return loadTableColumns(compactId, getTableColumnDefaults(compactId));
}

/** Column widths aligned with mobile Puts / Calls compact tables. */
const MOBILE_COL_WIDTHS: Record<string, number> = {
  broker_mark: 36,
  account_short: 56,
  underlying_ticker: 74,
  strike: 74,
  underlying_price: 80,
  decision: 72,
  expiration_date: 78,
  next_earnings_date: 72,
  mid: 72,
  collateral: 78,
  roi: 80,
  held: 64,
  open: 64,
  holding_avg_cost: 82,
  sfe_profitability: 80,
  _actions: 28,
};

function mobileColWidth(colId: string): number {
  return MOBILE_COL_WIDTHS[colId] ?? 72;
}

function mobileResetButtonHtml(id: string): string {
  return `<button type="button" class="sfe-mobile-reset-filters" id="${id}" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>`;
}

function mobileTickerFieldHtml(id: string, value: string): string {
  return `<label class="sfe-mobile-options3-field sfe-mobile-ticker-filter${value.trim() ? " selected" : ""}">
    <span class="sfe-mobile-sr-only">Filter ticker</span>
    <input id="${escAttr(id)}" type="text"${TICKER_FILTER_INPUT_ATTRS} placeholder="Tikr" value="${escAttr(value)}" aria-label="Filter ticker" />
  </label>`;
}

function mobileDteOptionsHtml(
  buckets: { key: string; label: string }[],
  selected: string,
): string {
  const allSelected = !selected || selected === "all" ? " selected" : "";
  const opts = buckets
    .map(
      (b) =>
        `<option value="${escAttr(b.key)}"${selected === b.key ? " selected" : ""}>${esc(b.label)}d</option>`,
    )
    .join("");
  return `<option value="all"${allSelected}>All</option>${opts}`;
}

function visibleColumnIds(
  columns: ColumnState[],
  tableSide: "puts" | "calls",
  compact = isWebCompactMode(),
): string[] {
  const lockedLast = "_actions";
  const ids = displayTableColumns(columns, compact).map((column) => column.key);
  if (!ids.includes(lockedLast)) ids.push(lockedLast);
  else {
    const i = ids.indexOf(lockedLast);
    if (i >= 0 && i !== ids.length - 1) {
      ids.splice(i, 1);
      ids.push(lockedLast);
    }
  }
  if (!ids.includes("underlying_ticker")) ids.unshift("underlying_ticker");
  return ids.filter((id) => id !== "dte");
}

function renderHeader(
  columns: ColumnState[],
  tableSide: "puts" | "calls",
  compact = isWebCompactMode(),
): string {
  const meta = new Map(
    (tableSide === "calls" ? Layout.CALL_COLUMN_META : Layout.PUT_COLUMN_META).map((m) => [m.id, m]),
  );
  const ids = visibleColumnIds(columns, tableSide, compact);
  return ids
    .map((colId) => {
      const m = EXTRA_COLUMN_META[colId] ?? meta.get(colId);
      if (colId === "_actions") return '<th data-col="_actions"></th>';
      if (!m) return "";
      const clsParts: string[] = [];
      if (m.num) clsParts.push("num");
      if (m.heldTh) clsParts.push("held-th");
      if (colId === "underlying_ticker") clsParts.push("recs-sticky-col", "sfe-ticker-col-header");
      if (colId === "next_earnings_date") clsParts.push("sfe-earnings-header");
      if (colId === "sfe_profitability") clsParts.push("sfe-profit-header");
      if (colId === "broker_mark" || colId === "account_short") clsParts.push("sfe-mark-col");
      if (colId === "account_short") clsParts.push("sfe-acct-col");
      const cls = clsParts.length ? ` class="${clsParts.join(" ")}"` : "";
      const sortKey = "sortKey" in m ? m.sortKey : undefined;
      const sortType = "sortType" in m ? m.sortType : undefined;
      const sortAttr = sortKey ? ` data-sort="${sortKey}" data-type="${sortType ?? "str"}"` : "";
      const hint = Layout.columnHeaderHint(m, compact);
      const tipAttr = hint ? ` data-tip="${escAttr(hint)}"` : "";
      return `<th data-col="${colId}"${sortAttr}${tipAttr}${cls}><span class="th-inner"><span class="th-label">${esc(m.label)}</span></span></th>`;
    })
    .join("");
}

function dash(colId: string, num = false): string {
  return `<td class="${num ? "num " : ""}muted" data-col="${colId}">—</td>`;
}

function expiryCell(rec: WheelRecommendation, compact: boolean): string {
  const inner = renderScreenerExpiryInnerHtml(rec, esc);
  const cls = compact ? "sfe-web-compact-cell" : "sfe-screener-expiry-cell";
  const tip = screenerExpiryEarningsTip(rec);
  const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
  const expDate = screenerExpiryDateLabel(rec.expiration_date);
  const dateAttr = expDate !== "—" ? ` data-expiration-date="${escAttr(expDate)}"` : "";
  return `<td class="${cls}" data-col="expiration_date"${dateAttr}${tipAttr}>${inner}</td>`;
}

function earningsCell(rec: WheelRecommendation, compact: boolean): string {
  const date = fmtEarningsDate(rec.next_earnings_date);
  const tip = earningsDateTip(rec);
  const within = earningsWithinExpiry(rec);
  const withinAttr = within ? ' data-earnings-within-expiry="true"' : "";
  const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
  const cls = compact ? "sfe-earnings-cell sfe-web-compact-cell" : "sfe-earnings-cell";
  if (date === "—") {
    return `<td class="${cls}" data-col="next_earnings_date"${tipAttr}${withinAttr}>—</td>`;
  }
  const inDays = fmtEarningsInDays(rec.next_earnings_date);
  const subCls = within ? "sfe-earnings-within-expiry" : "sfe-web-compact-sub";
  const inner = compact ? `${esc(date)}${compactSubline(inDays, subCls)}` : esc(date);
  return `<td class="${cls}" data-col="next_earnings_date"${tipAttr}${withinAttr}>${inner}</td>`;
}

function underlyingCell(
  rec: WheelRecommendation,
  idx: number,
  side: "puts" | "calls",
  _starred: Set<string>,
  compact = isWebCompactMode(),
): string {
  const ticker = rec.underlying_ticker ?? "—";
  const badges = renderIndexBadgesHtml(rec.index_badges);
  const advised = screenerRowIsAdvised(
    {
      underlying: rec.underlying_ticker,
      side: rec.side || (side === "calls" ? "call" : "put"),
      strike: rec.strike,
      expiration: rec.expiration_date,
      optionSymbol: rec.option_symbol,
    },
    side,
  );
  const adviceBadge = advised ? adviceBadgeHtml() : "";
  const tickerHtml = `<span class="recs-ticker" data-ticker="${escAttr(ticker)}" data-side="${side === "calls" ? "call" : "put"}" data-row-idx="${idx}" title="${escAttr(ticker)}">${esc(ticker)}<span class="tk-ic">↗</span></span>`;
  const compactCls = compact ? " sfe-web-compact-cell" : "";
  return `<td class="tk-cell${compactCls}" data-col="underlying_ticker"><div class="tk-row">${tickerHtml}${badges}${adviceBadge}</div></td>`;
}

function renderCell(
  colId: string,
  rec: WheelRecommendation,
  idx: number,
  side: "puts" | "calls",
  starred: Set<string>,
  compact = isWebCompactMode(),
): string {
  if (colId === "broker_mark") return brokerMarkCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  if (colId === "account_short") return accountShortCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  switch (colId) {
    case "underlying_ticker":
      return underlyingCell(rec, idx, side, starred, compact);
    case "market_cap":
    case "sector":
    case "target_price":
    case "decision":
    case "rec_quantity":
      return dash(colId, colId === "market_cap" || colId === "target_price" || colId === "rec_quantity");
    case "next_earnings_date":
      return earningsCell(rec, compact);
    case "strike": {
      if (side === "calls") {
        const vs = callStrikeVsAvgCost(rec.strike, rec.holding_avg_cost);
        const cls = callStrikeVsAvgValueClass(vs);
        const tip = callStrikeVsAvgTip(rec.strike, rec.holding_avg_cost);
        const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
        if (compact) {
          return `<td class="num sfe-web-compact-cell ${cls}" data-col="strike"${tipAttr}>${fmtNum(rec.strike, 2)}${compactSubline(`POP: ${fmtWheelRatio(rec.pop)}`, "sfe-web-compact-sub")}</td>`;
        }
        return `<td class="num ${cls}" data-col="strike"${tipAttr}>${fmtNum(rec.strike, 2)}</td>`;
      }
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="strike">${fmtNum(rec.strike, 2)}${compactSubline(`POP: ${fmtWheelRatio(rec.pop)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="strike">${fmtNum(rec.strike, 2)}</td>`;
    }
    case "underlying_price":
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="underlying_price">${fmtUsd(rec.underlying_price)}${compactSubline("tgt —", "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="underlying_price">${fmtUsd(rec.underlying_price)}</td>`;
    case "expiration_date":
      return expiryCell(rec, compact);
    case "dte":
      return `<td class="num" data-col="dte">${rec.dte != null ? rec.dte : "—"}</td>`;
    case "mid":
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="mid">${fmtUsd(rec.mid)}${compactSubline(`last ${fmtUsd(rec.last_price)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="mid">${fmtUsd(rec.mid)}</td>`;
    case "last_price":
      return `<td class="num" data-col="last_price">${fmtUsd(rec.last_price)}</td>`;
    case "collateral":
      return `<td class="num" data-col="collateral">${fmtUsd(rec.collateral)}</td>`;
    case "roi": {
      const v = Number(rec.roi);
      const cls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "";
      if (compact && side === "puts") {
        const subCls = cls || "sfe-web-compact-sub";
        return `<td class="num sfe-web-compact-cell" data-col="roi">${fmtWheelRatio(rec.roi)}${compactSubline(fmtPremiumPerDay(rec), subCls)}</td>`;
      }
      return `<td class="num ${cls}" data-col="roi">${fmtWheelRatio(rec.roi)}</td>`;
    }
    case "pop":
      return `<td class="num" data-col="pop">${fmtWheelRatio(rec.pop)}</td>`;
    case "held":
    case "open":
      if (rec.sfe_shares_held != null || rec.sfe_shares_available != null) {
        return renderScreenerSharesCellHtml(rec.sfe_shares_held, rec.sfe_shares_available, colId, esc);
      }
      return dash(colId, true);
    case "holding_avg_cost": {
      const avail = rec.sfe_shares_available;
      const availLabel =
        avail != null && Number.isFinite(avail) ? `avail ${fmtNum(avail, 0)}` : "—";
      const availCls = avail != null && avail > 0 ? "roi-strong" : "sfe-web-compact-sub";
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="holding_avg_cost">${fmtUsd(rec.holding_avg_cost)}${compactSubline(availLabel, availCls)}</td>`;
      }
      return `<td class="num" data-col="holding_avg_cost">${fmtUsd(rec.holding_avg_cost)}</td>`;
    }
    case "available_call_contracts":
      return `<td class="num" data-col="available_call_contracts">${rec.available_call_contracts != null ? rec.available_call_contracts : "—"}</td>`;
    case "sfe_profitability": {
      const result = recommendationProfitability(rec);
      if (compact) {
        const v = Number(rec.roi);
        const roiCls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "sfe-web-compact-sub";
        return `<td class="num sfe-web-compact-cell sfe-profit-cell" data-col="sfe_profitability" data-tip="${escAttr(result.tip)}">${fmtProfitPct(result.pct)}${compactSubline(fmtWheelRatio(rec.roi), roiCls)}</td>`;
      }
      return `<td class="num sfe-profit-cell" data-col="sfe_profitability" data-tip="${escAttr(result.tip)}">${fmtProfitPct(result.pct)}</td>`;
    }
    case "sfe_max_loss":
    case "sfe_profit":
      return dash(colId, true);
    case "_actions":
      return `<td class="pos-actions" data-col="_actions">${renderFocusRecRowMenu(
        {
          ...rec,
          side: rec.side || (side === "calls" ? "call" : "put"),
        },
        side === "calls" ? "calls-plus" : "puts-plus",
      )}</td>`;
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

function renderRow(
  rec: WheelRecommendation,
  index: number,
  columns: ColumnState[],
  side: "puts" | "calls",
  starred: Set<string>,
  compact = isWebCompactMode(),
): string {
  const ids = visibleColumnIds(columns, side, compact);
  const cells = ids.map((id) => renderCell(id, rec, index, side, starred, compact)).join("");
  const advised = screenerRowIsAdvised(
    {
      underlying: rec.underlying_ticker,
      side: rec.side || (side === "calls" ? "call" : "put"),
      strike: rec.strike,
      expiration: rec.expiration_date,
      optionSymbol: rec.option_symbol,
    },
    side,
  );
  const key = screenerContractKey({
    underlying: rec.underlying_ticker,
    side: rec.side || (side === "calls" ? "call" : "put"),
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  });
  return `<tr class="${advised ? "sfe-row-advised-line" : ""}" ${adviceRowAttrs({
    underlying: rec.underlying_ticker,
    side: rec.side || (side === "calls" ? "call" : "put"),
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  })} data-row-key="${escAttr(key)}" data-row-idx="${index}">${cells}</tr>`;
}

function renderMobilePutFilters(state: PutFilterState, starredOn: boolean, cashOn: boolean): string {
  const dteSelected = state.dte.length === 1 ? state.dte[0]! : "all";
  const dteActive = dteSelected !== "all";
  return `
    <div class="sfe-mobile-options3-filters sfe-mobile-puts1-filters-panel">
      ${mobileTickerFieldHtml("tradierPutTicker", state.ticker)}
      <div class="sfe-mobile-options3-segments sfe-mobile-puts1-starred-segments" role="group" aria-label="Starred / Focus watchlist">
        <button type="button" class="${starredOn ? "" : "active"}" data-tradier-put-starred="off">Off</button>
        <button type="button" class="${starredOn ? "active" : ""}" data-tradier-put-starred="on">On</button>
      </div>
      <label class="sfe-mobile-options3-field sfe-mobile-options3-dte-select${dteActive ? " selected" : ""}">
        <span class="sfe-mobile-sr-only">Expiration</span>
        <select id="tradierPutDteSingle" aria-label="Days to expiration">
          ${mobileDteOptionsHtml(PUT_DTE_BUCKETS, dteSelected)}
        </select>
      </label>
      <button
        type="button"
        class="sfe-mobile-puts1-liq${state.quote === "tradable" ? " active" : ""}"
        data-tradier-put-quote-toggle
        aria-pressed="${state.quote === "tradable" ? "true" : "false"}"
        title="Only show quotes where mid and last are close (liquid)"
      >Liq</button>
      <div class="sfe-mobile-options3-segments sfe-mobile-puts1-quote-segments" role="group" aria-label="Earnings filter">
        <button type="button" class="${state.earnings === "before" ? "active" : ""}" data-tradier-put-earnings="before">Pre</button>
        <button type="button" class="${state.earnings === "all" ? "active" : ""}" data-tradier-put-earnings="all">All</button>
      </div>
      <button
        type="button"
        class="sfe-mobile-puts1-liq${cashOn ? " active" : ""}"
        data-tradier-put-cash-toggle
        aria-pressed="${cashOn ? "true" : "false"}"
        title="Only show puts whose collateral fits available cash"
      >Cash</button>
      <label class="sfe-mobile-options3-field${state.minProfitPct > 0 ? " selected" : ""}">
        <span class="sfe-mobile-sr-only">Min profit %</span>
        <input id="tradierPutMinProfit" type="number" min="0" step="0.1" inputmode="decimal" placeholder="Profit%" value="${state.minProfitPct > 0 ? state.minProfitPct : ""}" aria-label="Min profit percent" />
      </label>
      <div class="sfe-mobile-filter-reset-group">
        ${mobileResetButtonHtml("tradierPutResetFilters")}
      </div>
    </div>`;
}

function renderMobileCallFilters(state: CallFilterState, allRows: WheelRecommendation[]): string {
  const dteBuckets = CALL_DTE_BUCKETS.filter((b) =>
    allRows.some((r) => {
      const n = Number(r.dte);
      return Number.isFinite(n) && n >= b.min && n <= b.max;
    }),
  );
  const dteOptions = dteBuckets.length ? dteBuckets : CALL_DTE_BUCKETS;
  const dteSelected = state.dte || "all";
  const dteActive = dteSelected !== "all";
  return `
    <div class="sfe-mobile-options3-filters sfe-mobile-calls1-filters-panel">
      ${mobileTickerFieldHtml("tradierCallTicker", state.ticker)}
      <label class="sfe-mobile-options3-field sfe-mobile-options3-dte-select${dteActive ? " selected" : ""}">
        <span class="sfe-mobile-sr-only">Expiration</span>
        <select id="tradierCallDteSingle" aria-label="Days to expiration">
          ${mobileDteOptionsHtml(dteOptions, dteSelected)}
        </select>
      </label>
      <button
        type="button"
        class="sfe-mobile-calls1-liq${state.quote === "tradable" ? " active" : ""}"
        data-tradier-call-liq-toggle
        aria-pressed="${state.quote === "tradable" ? "true" : "false"}"
        title="Only show quotes where mid and last are close (liquid)"
      >Liq</button>
      <div class="sfe-mobile-options3-segments sfe-mobile-calls1-shares-segments" role="group" aria-label="Share capacity">
        <button type="button" class="${state.shares === "all" ? "active" : ""}" data-tradier-call-shares="all">All</button>
        <button type="button" class="${state.shares === "available" ? "active" : ""}" data-tradier-call-shares="available">Avail</button>
      </div>
      <button
        type="button"
        class="sfe-mobile-calls1-liq${state.risky ? " active" : ""}"
        data-tradier-call-risky-toggle
        aria-pressed="${state.risky ? "true" : "false"}"
        title="Include covered calls with strike below average cost"
      >Risky</button>
      ${
        state.risky
          ? `<div class="sfe-mobile-calls1-risky-pop">${renderCallsRiskyPopRangeSlider(state.riskyPopRange, { id: "tradierCallRiskyPop" })}</div>`
          : ""
      }
      <div class="sfe-mobile-filter-reset-group">
        ${mobileResetButtonHtml("tradierCallResetFilters")}
      </div>
    </div>`;
}

function renderPutFilters(state: PutFilterState, allRows: WheelRecommendation[], starredOn: boolean, cashOn: boolean): string {
  return `
    <div class="sfe-puts-filter-rows" data-tour-anchor="wheel-tradier-puts-filters">
      <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="tradierPutTicker">Ticker<span class="hint" tabindex="0" role="img" data-tip="Prefix match — case-insensitive.">i</span></label>
        <input type="text" id="tradierPutTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label>Starred<span class="hint" tabindex="0" role="img" data-tip="Show only tickers you starred.">i</span></label>
        <div class="year-chips"><button type="button" class="year-chip${starredOn ? " active" : ""}" data-tradier-put-starred-toggle aria-pressed="${starredOn ? "true" : "false"}">Starred</button></div>
      </div>
      <div class="filter-field">
        <label>Earnings<span class="hint" tabindex="0" role="img" data-tip="Hide expirations on or after the next Tradier earnings date.">i</span></label>
        <div class="year-chips"><button type="button" class="year-chip${state.earnings === "before" ? " active" : ""}" data-tradier-put-earnings-toggle aria-pressed="${state.earnings === "before" ? "true" : "false"}">Before earnings</button></div>
      </div>
      <div class="filter-field">
        <label>Quote<span class="hint" tabindex="0" role="img" data-tip="Only show contracts with a recent two-sided quote.">i</span></label>
        <div class="year-chips"><button type="button" class="year-chip${state.quote === "tradable" ? " active" : ""}" data-tradier-put-quote-toggle aria-pressed="${state.quote === "tradable" ? "true" : "false"}">Tradable</button></div>
      </div>
      <div class="filter-field">
        <label>Cash limit<span class="hint" tabindex="0" role="img" data-tip="Only show puts whose collateral fits available cash from the Robinhood snapshot.">i</span></label>
        <div class="year-chips"><button type="button" class="year-chip${cashOn ? " active" : ""}" data-tradier-put-cash-toggle aria-pressed="${cashOn ? "true" : "false"}">Cash limit</button></div>
      </div>
      ${advisoryFilterFieldHtml()}
      <div class="filter-field">
        <label for="tradierPutDteFilter">DTE<span class="hint" tabindex="0" role="img" data-tip="Filter by days-to-expiration bucket. Max DTE cutoff lives in Settings.">i</span></label>
        <select multiple id="tradierPutDteFilter" aria-label="Puts+ DTE filter" data-placeholder="All DTE">${multiSelectOptionsWithAll("All DTE", DTE_OPTIONS, dteSelectionForUi(state.dte))}</select>
      </div>
      <div class="filter-field">
        <label for="tradierPutIndexFilter">Index<span class="hint" tabindex="0" role="img" data-tip="Filter by index membership.">i</span></label>
        <select multiple id="tradierPutIndexFilter" aria-label="Puts+ index filter" data-placeholder="All indexes">${multiSelectOptionsWithAll("All indexes", INDEX_OPTIONS, indexesSelectionForUi(state.indexes))}</select>
      </div>
      <div class="filter-field">
        <label for="tradierPutCapFilter">Cap<span class="hint" tabindex="0" role="img" data-tip="Market-cap bands — Tradier rows omit Finviz cap, so Unknown matches all.">i</span></label>
        <select multiple id="tradierPutCapFilter" aria-label="Puts+ market cap filter" data-placeholder="All caps">${multiSelectOptionsWithAll("All caps", CAP_OPTIONS, capsSelectionForUi(state.caps))}</select>
      </div>
      </div>
      <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="tradierPutSectorFilter">Sector<span class="hint" tabindex="0" role="img" data-tip="Sector is omitted from Tradier; Unknown covers all names.">i</span></label>
        <select multiple id="tradierPutSectorFilter" aria-label="Puts+ sector filter" data-placeholder="All sectors">${sectorOptions(allRows, state.sectors)}</select>
      </div>
      <div class="filter-field">
        <label for="tradierPutRubricFilter">Rubric band<span class="hint" tabindex="0" role="img" data-tip="Rubric reports are omitted on Puts+.">i</span></label>
        <select multiple id="tradierPutRubricFilter" aria-label="Puts+ rubric band filter" data-placeholder="All bands">${multiSelectOptionsWithAll("All bands", RUBRIC_OPTIONS, state.rubricBands)}</select>
      </div>
      <div class="filter-field">
        <label for="tradierPutMinProfit">Min profit.<span class="hint" tabindex="0" role="img" data-tip="Minimum premium yield on cash collateral: (mid × 100) ÷ collateral.">i</span></label>
        <span class="filter-affix">
          <input type="number" id="tradierPutMinProfit" class="filter-input-compact" step="0.1" min="0" inputmode="decimal" aria-label="Minimum profitability percent" value="${state.minProfitPct > 0 ? state.minProfitPct : ""}" />
          <span class="filter-affix__suffix">%</span>
        </span>
      </div>
      <div class="filter-field">
        <div class="sfe-filter-reset-actions sfe-web-tradier-puts-columns-host">
          <button type="button" class="small sfe-reset-filters" id="tradierPutResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          ${screenerAdviceButtonHtml()}
        </div>
      </div>
      </div>
    </div>`;
}

function renderCallFilters(state: CallFilterState, allRows: WheelRecommendation[]): string {
  const dteBuckets = CALL_DTE_BUCKETS.filter((b) =>
    allRows.some((r) => {
      const n = Number(r.dte);
      return Number.isFinite(n) && n >= b.min && n <= b.max;
    }),
  );
  const dteChips = [
    `<button type="button" class="year-chip${state.dte === "all" ? " active" : ""}" data-tradier-call-dte="all">All</button>`,
    ...dteBuckets.map(
      (b) =>
        `<button type="button" class="year-chip${state.dte === b.key ? " active" : ""}" data-tradier-call-dte="${escAttr(b.key)}">${esc(b.label)}</button>`,
    ),
  ].join("");
  return `
    <div class="filter-bar recs-filter-bar" data-tour-anchor="wheel-tradier-calls-filters">
      <div class="filter-field">
        <label for="tradierCallTicker">Ticker<span class="hint" tabindex="0" role="img" data-tip="Prefix match — case-insensitive.">i</span></label>
        <input type="text" id="tradierCallTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label>Quote<span class="hint" tabindex="0" role="img" data-tip="Tradable = recent two-sided quote; All also shows wider spreads.">i</span></label>
        <div class="year-chips">
          <button type="button" class="year-chip${state.quote === "tradable" ? " active" : ""}" data-tradier-call-quote="tradable">Tradable</button>
          <button type="button" class="year-chip${state.quote === "all" ? " active" : ""}" data-tradier-call-quote="all">All</button>
        </div>
      </div>
      <div class="filter-field">
        <label>DTE<span class="hint" tabindex="0" role="img" data-tip="Filter by days-to-expiration bucket. Max DTE cutoff lives in Settings.">i</span></label>
        <div class="year-chips">${dteChips}</div>
      </div>
      <div class="filter-field">
        <label>Shares<span class="hint" tabindex="0" role="img" data-tip="Available shows tickers with free share-backed call contracts.">i</span></label>
        <div class="year-chips">
          <button type="button" class="year-chip${state.shares === "all" ? " active" : ""}" data-tradier-call-shares="all">All</button>
          <button type="button" class="year-chip${state.shares === "available" ? " active" : ""}" data-tradier-call-shares="available">Available</button>
        </div>
      </div>
      ${advisoryFilterFieldHtml()}
      <div class="filter-field">
        <label>Risky<span class="hint" tabindex="0" role="img" data-tip="Include covered-call strikes below average share cost. Tradier’s board already prefers strike ≥ avg; turn on to keep any below-avg rows that appear.">i</span></label>
        <div class="year-chips"><button type="button" class="year-chip${state.risky ? " active" : ""}" data-tradier-call-risky-toggle aria-pressed="${state.risky ? "true" : "false"}">Risky</button></div>
      </div>
      ${
        state.risky
          ? `<div class="filter-field sfe-calls-risky-pct-field">
        <label for="tradierCallRiskyPopMin">PoP<span class="hint" tabindex="0" role="img" data-tip="Show below-avg suggestions whose PoP falls between the two handles.">i</span></label>
        ${renderCallsRiskyPopRangeSlider(state.riskyPopRange, { id: "tradierCallRiskyPop" })}
      </div>`
          : ""
      }
      <div class="filter-field">
        <div class="sfe-filter-reset-actions sfe-web-tradier-calls-columns-host">
          <button type="button" class="small sfe-reset-filters" id="tradierCallResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          ${screenerAdviceButtonHtml()}
        </div>
      </div>
    </div>`;
}

function applyPutFilters(
  rows: WheelRecommendation[],
  state: PutFilterState,
  starredOn: boolean,
  starred: Set<string>,
  cashOn: boolean,
  availableCash: number | null,
): WheelRecommendation[] {
  let out = Layout.filterRecommendationRows(
    rows,
    state,
    "put",
    PUT_DTE_BUCKETS,
    isTradableQuote,
  ) as WheelRecommendation[];
  out = out.filter((row) => scopeAllowsRecommendation(row.underlying_ticker, "put"));
  if (starredOn) out = out.filter((r) => starred.has((r.underlying_ticker ?? "").toUpperCase()));
  if (cashOn && availableCash != null) {
    out =
      availableCash <= 0
        ? []
        : out.filter((r) => (Number(r.collateral) || 0) <= availableCash + 1e-6);
  }
  if (state.minProfitPct > 0) {
    out = out.filter((r) => {
      const profit = recommendationProfitPct(r);
      return profit != null && profit >= state.minProfitPct;
    });
  }
  return out;
}

function applyCallFilters(rows: WheelRecommendation[], state: CallFilterState): WheelRecommendation[] {
  let out = Layout.filterRecommendationRows(
    rows,
    state,
    "call",
    CALL_DTE_BUCKETS,
    isTradableQuote,
  ) as WheelRecommendation[];
  out = out.filter((row) => scopeAllowsRecommendation(row.underlying_ticker));
  if (state.shares === "available") {
    out = out.filter((r) => (r.sfe_shares_available ?? 0) > 0);
  }
  if (!state.risky) {
    out = out.filter((r) => !isRiskyCallRecommendation(r));
  } else {
    out = out.filter(
      (r) => !isRiskyCallRecommendation(r) || meetsRiskyPopRange(r.pop, state.riskyPopRange),
    );
  }
  return out;
}

export function mountTradierWheelPanel(host: HTMLElement, side: "puts" | "calls"): () => void {
  let disposed = false;
  let timer = 0;
  let allRows: WheelRecommendation[] = [];
  let viewMeta: TradierWheelView | null = null;
  let error = "";
  let putState = loadPutFilters();
  let callState = loadCallFilters();
  let starred = loadLocalStarred();
  let starredOn = localStorage.getItem(PUT_STARRED_FILTER_KEY) === "1";
  let cashOn = localStorage.getItem(PUT_CASH_LIMIT_FILTER_KEY) === "1";
  let availableCash: number | null = null;
  const tableId = side === "calls" ? CALL_TABLE_ID : PUT_TABLE_ID;
  const unsubCompact = subscribeWebCompactMode(() => paint());
  const unsubMarks = subscribeLedgerMarks(() => paint());
  const unsubScope = subscribeAccountScope(() => paint());

  const statusText = (filteredCount: number): string => {
    if (error) return error;
    if (!viewMeta?.configured && viewMeta?.message) return viewMeta.message;
    const scan = viewMeta?.scanComplete
      ? `${filteredCount} shown · scan ${viewMeta.scanned ?? 0}/${viewMeta.universe ?? 0}`
      : `Opening scan ${viewMeta?.scanned ?? 0}/${viewMeta?.universe ?? 0}`;
    const updated = viewMeta?.updatedAt ? ` · updated ${new Date(viewMeta.updatedAt).toLocaleTimeString()}` : "";
    return `${scan}${updated}. Min PoP / Max DTE live in Settings.`;
  };

  function paint(): void {
    if (disposed) return;
    const mobile = isMobileTradierHost(host);
    const compact = useCompactCells(host);
    const columns = resolveTradierColumns(tableId, mobile);
    const filtered =
      side === "puts"
        ? applyPutFilters(allRows, putState, starredOn, starred, cashOn, availableCash)
        : applyCallFilters(allRows, callState);
    const colIds = visibleColumnIds(columns, side, compact);
    const head = renderHeader(columns, side, compact);
    const emptyMsg =
      viewMeta?.message ||
      (allRows.length ? "No contracts match your filters." : "No contracts in the Tradier cache yet.");
    const body = filtered.length
      ? filtered.map((rec, i) => renderRow(rec, i, columns, side, starred, compact)).join("")
      : mobile
        ? ""
        : `<tr><td colspan="${Math.max(colIds.length, 1)}" class="muted">${esc(emptyMsg)}</td></tr>`;
    const filters = mobile
      ? side === "puts"
        ? renderMobilePutFilters(putState, starredOn, cashOn)
        : renderMobileCallFilters(callState, allRows)
      : side === "puts"
        ? renderPutFilters(putState, allRows, starredOn, cashOn)
        : renderCallFilters(callState, allRows);
    const tableClass = side === "puts" ? "wheel-screener-puts" : "wheel-screener-calls";
    const tip =
      side === "puts"
        ? "S&P 500 cash-secured puts from Tradier. One contract per name, highest CreditW, top 120. Min PoP and Max DTE are in Settings."
        : "Covered calls from Tradier on shares you hold. Min PoP and Max DTE are in Settings.";
    const title = side === "puts" ? "Puts+" : "Calls+";
    const tableIdAttr = `tradier${side === "puts" ? "Puts" : "Calls"}Table`;

    if (mobile) {
      const tableWidth = colIds.reduce((sum, id) => sum + mobileColWidth(id), 0);
      const colGroup = `<colgroup>${colIds
        .map((id) => `<col style="width:${mobileColWidth(id)}px" />`)
        .join("")}</colgroup>`;
      const wrapClass =
        side === "puts" ? "sfe-mobile-puts1-table-wrap" : "sfe-mobile-calls1-table-wrap";
      const tableHtml = filtered.length
        ? `<div class="sfe-mobile-options-table-wrap ${wrapClass}" data-tradier-table="1"><table class="sfe-mobile-options-table ${tableClass}" id="${tableIdAttr}"${renderTablePinnedAttr(columns)} style="width:${tableWidth}px;min-width:${tableWidth}px">${colGroup}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`
        : `<div class="sfe-mobile-empty">${esc(emptyMsg)}</div>`;
      host.innerHTML = `
        <div data-wheel-view="${side === "puts" ? "puts-plus" : "calls-plus"}" class="sfe-mobile-tradier-panel sfe-tradier-wheel">
          <div class="sfe-mobile-tab-section-head"><h2>${title}</h2></div>
          ${filters}
          <p class="sfe-mobile-tradier-status muted small" data-tradier-status>${esc(statusText(filtered.length))}</p>
          ${tableHtml}
        </div>`;
    } else {
      host.innerHTML = `
        <div data-wheel-view="${side === "puts" ? "puts-plus" : "calls-plus"}" class="panel sfe-web-native-${side === "puts" ? "puts" : "calls"} sfe-tradier-wheel" style="margin-top:12px;">
          <h2>${title}<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></h2>
          ${filters}
          <p class="muted small recs-filter-note" data-tradier-status>${esc(statusText(filtered.length))}</p>
          <div class="recs-wrap">
            <div class="recs-hscroll">
              <table class="recs recs-compact recs-sticky-col ${tableClass}" id="${tableIdAttr}"${renderTablePinnedAttr(columns)}>
                <thead><tr>${head}</tr></thead>
                <tbody>${body}</tbody>
              </table>
            </div>
          </div>
        </div>`;
    }
    bind(filtered);
    if (!mobile) enhanceMultiSelects(host);
    applyAdviceMarks(host, side);
    bindScreenerAdvice(host, side);
    if (!mobile) {
      const resetBtn = host.querySelector<HTMLElement>(
        side === "puts" ? "#tradierPutResetFilters" : "#tradierCallResetFilters",
      );
      if (resetBtn) {
        ensureColumnPickerBesideReset(resetBtn, {
          tableId,
          title: side === "puts" ? "Configure Puts+ columns" : "Configure Calls+ columns",
          defaults: getTableColumnDefaults(tableId),
          onApply: () => paint(),
        });
      }
    }
  }

  function bind(_filtered: WheelRecommendation[]): void {
    guardTickerFilterInputs(host);
    if (side === "puts") {
      host.querySelector("#tradierPutTicker")?.addEventListener("input", (event) => {
        const input = event.target as HTMLInputElement;
        putState.ticker = sanitizeTickerFilterValue(input.value).toUpperCase();
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
      host.querySelector("[data-tradier-put-starred-toggle]")?.addEventListener("click", () => {
        starredOn = !starredOn;
        localStorage.setItem(PUT_STARRED_FILTER_KEY, starredOn ? "1" : "0");
        paint();
      });
      host.querySelectorAll("[data-tradier-put-starred]").forEach((btn) => {
        btn.addEventListener("click", () => {
          starredOn = (btn as HTMLElement).dataset.tradierPutStarred === "on";
          localStorage.setItem(PUT_STARRED_FILTER_KEY, starredOn ? "1" : "0");
          paint();
        });
      });
      host.querySelector("[data-tradier-put-earnings-toggle]")?.addEventListener("click", () => {
        putState.earnings = putState.earnings === "before" ? "all" : "before";
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
      host.querySelectorAll("[data-tradier-put-earnings]").forEach((btn) => {
        btn.addEventListener("click", () => {
          putState.earnings =
            (btn as HTMLElement).dataset.tradierPutEarnings === "before" ? "before" : "all";
          saveJson(PUT_FILTERS_KEY, putState);
          paint();
        });
      });
      host.querySelector("[data-tradier-put-quote-toggle]")?.addEventListener("click", () => {
        putState.quote = putState.quote === "tradable" ? "all" : "tradable";
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
      host.querySelector("[data-tradier-put-cash-toggle]")?.addEventListener("click", () => {
        cashOn = !cashOn;
        localStorage.setItem(PUT_CASH_LIMIT_FILTER_KEY, cashOn ? "1" : "0");
        if (cashOn) void refreshCash();
        else {
          availableCash = null;
          paint();
        }
      });
      host.querySelector("#tradierPutDteSingle")?.addEventListener("change", (event) => {
        const value = (event.target as HTMLSelectElement).value || "all";
        putState.dte = value === "all" ? [] : [value];
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
      const bindMulti = (id: string, assign: (values: string[]) => void) => {
        host.querySelector(`#${id}`)?.addEventListener("change", (event) => {
          assign(readMultiValues(event.target as HTMLSelectElement));
          saveJson(PUT_FILTERS_KEY, putState);
          paint();
        });
      };
      bindMulti("tradierPutDteFilter", (v) => {
        putState.dte = v;
      });
      bindMulti("tradierPutIndexFilter", (v) => {
        putState.indexes = v;
      });
      bindMulti("tradierPutCapFilter", (v) => {
        putState.caps = v;
      });
      bindMulti("tradierPutSectorFilter", (v) => {
        putState.sectors = v;
      });
      bindMulti("tradierPutRubricFilter", (v) => {
        putState.rubricBands = v;
      });
      host.querySelector("#tradierPutMinProfit")?.addEventListener("change", (event) => {
        const n = Number((event.target as HTMLInputElement).value);
        putState.minProfitPct = Number.isFinite(n) && n > 0 ? n : 0;
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
      host.querySelector("#tradierPutResetFilters")?.addEventListener("click", () => {
        putState = defaultPutFilters();
        starredOn = false;
        cashOn = false;
        localStorage.setItem(PUT_STARRED_FILTER_KEY, "0");
        localStorage.setItem(PUT_CASH_LIMIT_FILTER_KEY, "0");
        saveJson(PUT_FILTERS_KEY, putState);
        paint();
      });
    } else {
      host.querySelector("#tradierCallTicker")?.addEventListener("input", (event) => {
        callState.ticker = sanitizeTickerFilterValue((event.target as HTMLInputElement).value).toUpperCase();
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      });
      host.querySelectorAll("[data-tradier-call-quote]").forEach((btn) => {
        btn.addEventListener("click", () => {
          callState.quote = (btn as HTMLElement).dataset.tradierCallQuote === "all" ? "all" : "tradable";
          saveJson(CALL_FILTERS_KEY, callState);
          paint();
        });
      });
      host.querySelector("[data-tradier-call-liq-toggle]")?.addEventListener("click", () => {
        callState.quote = callState.quote === "tradable" ? "all" : "tradable";
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      });
      host.querySelectorAll("[data-tradier-call-dte]").forEach((btn) => {
        btn.addEventListener("click", () => {
          callState.dte = (btn as HTMLElement).dataset.tradierCallDte || "all";
          saveJson(CALL_FILTERS_KEY, callState);
          paint();
        });
      });
      host.querySelector("#tradierCallDteSingle")?.addEventListener("change", (event) => {
        callState.dte = (event.target as HTMLSelectElement).value || "all";
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      });
      host.querySelectorAll("[data-tradier-call-shares]").forEach((btn) => {
        btn.addEventListener("click", () => {
          callState.shares =
            (btn as HTMLElement).dataset.tradierCallShares === "available" ? "available" : "all";
          saveJson(CALL_FILTERS_KEY, callState);
          paint();
        });
      });
      host.querySelector("[data-tradier-call-risky-toggle]")?.addEventListener("click", () => {
        callState.risky = !callState.risky;
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      });
      host.querySelector("#tradierCallResetFilters")?.addEventListener("click", () => {
        callState = defaultCallFilters();
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      });
      const riskyMin = host.querySelector<HTMLInputElement>("#tradierCallRiskyPopMin");
      const riskyMax = host.querySelector<HTMLInputElement>("#tradierCallRiskyPopMax");
      const onRisky = () => {
        callState.riskyPopRange = parseCallsRiskyPopRange({
          min: Number(riskyMin?.value),
          max: Number(riskyMax?.value),
        });
        saveJson(CALL_FILTERS_KEY, callState);
        paint();
      };
      riskyMin?.addEventListener("change", onRisky);
      riskyMax?.addEventListener("change", onRisky);
    }
  }

  async function refreshCash(): Promise<void> {
    try {
      availableCash = await loadAvailableCashFromAgenticSnapshot(fetchAgenticSnapshot);
    } catch {
      availableCash = null;
    }
    if (!disposed) paint();
  }

  async function load(): Promise<void> {
    try {
      await ensureLedgerMarkIndex().catch(() => null);
      const [view, positions] = await Promise.all([
        fetchTradierWheel(side),
        loadPositionsSnapshot(false).catch(() => peekPositionsSnapshot()),
      ]);
      if (disposed) return;
      viewMeta = view;
      error = "";
      allRows = (view.recommendations ?? [])
        .map((row) => toRec(row, side))
        .map((rec) => enrichShares(rec, positions));
      paint();
    } catch (err) {
      if (disposed) return;
      error = err instanceof Error ? err.message : "Could not load Tradier recommendations.";
      paint();
    }
  }

  const onSettingsChanged = (): void => {
    void load();
  };
  window.addEventListener(TRADIER_WHEEL_SETTINGS_CHANGED_EVENT, onSettingsChanged);

  const filterTarget = side === "calls" ? "calls-plus" : "puts-plus";
  const onTickerFilterEvent = (event: Event): void => {
    const detail = (event as CustomEvent<{ target?: string; ticker?: string }>).detail;
    if (!detail || detail.target !== filterTarget) return;
    const next = sanitizeTickerFilterValue(String(detail.ticker ?? "")).toUpperCase();
    if (side === "puts") {
      putState = { ...putState, ticker: next };
      saveJson(PUT_FILTERS_KEY, putState);
    } else {
      callState = { ...callState, ticker: next };
      saveJson(CALL_FILTERS_KEY, callState);
    }
    paint();
  };
  document.addEventListener(WHEEL_TICKER_FILTER_EVENT, onTickerFilterEvent);

  if (cashOn && side === "puts") void refreshCash();
  void load();
  timer = window.setInterval(() => void load(), 60_000);

  return () => {
    disposed = true;
    window.clearInterval(timer);
    window.removeEventListener(TRADIER_WHEEL_SETTINGS_CHANGED_EVENT, onSettingsChanged);
    document.removeEventListener(WHEEL_TICKER_FILTER_EVENT, onTickerFilterEvent);
    unsubCompact();
    unsubMarks();
    unsubScope();
  };
}
